/**
 * 環境変数設定管理
 * 必須値の検証とランタイム設定の取得を提供します
 *
 * Vite設定で公開を許可したVITE_PUBLIC_*だけを参照します。
 * process.env[key]のような動的アクセスはビルド時置換されないため使用しません。
 */

/** サポート対象の環境名 */
const SUPPORTED_APP_ENVS = ['development', 'staging', 'production'];

/** デフォルト環境 */
const DEFAULT_APP_ENV = 'development';

/** デフォルトログレベル */
const DEFAULT_LOG_LEVEL = 'info';

/** キャッシュ済み設定 */
let cachedConfig = null;

/**
 * 公開環境変数を取得する
 * @param {string} envName - 環境変数名
 * @param {string | undefined} staticValue - Viteビルド時にインライン置換される値
 * @returns {string} 環境変数値
 */
const readPublicEnv = (envName, staticValue) => {
  if (process.env.NODE_ENV === 'test') {
    return normalizeString(process.env[envName]);
  }

  return normalizeString(staticValue);
};

/**
 * 文字列値の前後空白を除去して返す
 * @param {string | undefined} value - 変換対象
 * @returns {string} 正規化済み文字列
 */
const normalizeString = (value) => {
  if (!value) {
    return '';
  }

  return String(value).trim();
};

/**
 * 環境名を正規化する
 * @param {string} appEnv - 環境名
 * @returns {'development' | 'staging' | 'production'} 正規化済み環境名
 */
const normalizeAppEnv = (appEnv) => {
  if (SUPPORTED_APP_ENVS.includes(appEnv)) {
    return /** @type {'development' | 'staging' | 'production'} */ (appEnv);
  }

  return DEFAULT_APP_ENV;
};

/**
 * ランタイム設定を取得する
 * Viteビルド時のインライン置換に対応するため、
 * 環境変数は静的リテラルで直接アクセスする
 * @returns {Object} アプリ設定
 */
export const getPublicRuntimeConfig = () => {
  if (cachedConfig) {
    return cachedConfig;
  }

  /* Viteのビルド時インライン置換のため、静的にアクセスする */

  /** Supabase URL（必須） */
  const supabaseUrl = readPublicEnv(
    'VITE_PUBLIC_SUPABASE_URL',
    process.env.VITE_PUBLIC_SUPABASE_URL,
  );

  /** Supabase Anonキー（必須） */
  const supabaseAnonKey = readPublicEnv(
    'VITE_PUBLIC_SUPABASE_ANON_KEY',
    process.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
  );

  if (!supabaseUrl) {
    throw new Error('必須環境変数が未設定です: VITE_PUBLIC_SUPABASE_URL');
  }

  if (!supabaseAnonKey) {
    throw new Error('必須環境変数が未設定です: VITE_PUBLIC_SUPABASE_ANON_KEY');
  }

  /** 環境名 */
  const appEnvCandidate = readPublicEnv('VITE_PUBLIC_APP_ENV', process.env.VITE_PUBLIC_APP_ENV)
    || readPublicEnv('APP_ENV', process.env.APP_ENV)
    || DEFAULT_APP_ENV;

  /** 正規化済み環境名 */
  const appEnv = normalizeAppEnv(appEnvCandidate);

  /** ログレベル */
  const logLevel = readPublicEnv('VITE_PUBLIC_LOG_LEVEL', process.env.VITE_PUBLIC_LOG_LEVEL)
    || DEFAULT_LOG_LEVEL;

  /** 公開アプリURL */
  const appBaseUrl = readPublicEnv('VITE_PUBLIC_APP_BASE_URL', process.env.VITE_PUBLIC_APP_BASE_URL)
    || '';

  /** GAS WebアプリURL */
  const gasWebAppUrl = readPublicEnv(
    'VITE_PUBLIC_GAS_WEB_APP_URL',
    process.env.VITE_PUBLIC_GAS_WEB_APP_URL,
  ) || '';

  /**
   * 画像配信オリジンの差し替え先（未設定時は現行のSupabase直URLへフォールバック）
   * 将来Cloudflare経由のプロキシパスへ切り替える際に、この1値の変更だけで済むようにする
   */
  const imageOrigin = readPublicEnv(
    'VITE_PUBLIC_IMAGE_ORIGIN',
    process.env.VITE_PUBLIC_IMAGE_ORIGIN,
  ) || '';

  /** 構築した設定 */
  const nextConfig = {
    appEnv,
    appBaseUrl,
    logLevel,
    supabase: {
      url: supabaseUrl,
      anonKey: supabaseAnonKey,
    },
    gas: {
      webAppUrl: gasWebAppUrl,
    },
    imageOrigin,
  };

  cachedConfig = nextConfig;
  return nextConfig;
};

/**
 * テスト用途でキャッシュ済み設定を初期化する
 * @returns {void}
 */
export const resetRuntimeConfigCache = () => {
  cachedConfig = null;
};
